import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { toast } from 'react-toastify';
import {
    ChevronRight, User, FileCheck, AlertCircle, Trash2, Download, Mail,
    FileText, LogOut, Users, Search, Power, X
} from 'lucide-react';
import {
    IoAddCircleOutline, IoSaveOutline, IoClose, IoCheckboxOutline,
    IoSquareOutline, IoTrashOutline, IoDocumentTextOutline, IoMailOutline,
    IoInformationCircleOutline, IoCheckmarkDoneOutline, IoSearch,
    IoEye, IoPencil, IoChevronForward
} from 'react-icons/io5';
import './Offboarding.css';

const API_BASE = API_URL;

const OffboardingDashboard = () => {
    const [requests, setRequests] = useState([]);
    const [selectedReq, setSelectedReq] = useState(null);
    const [checklist, setChecklist] = useState([]);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionModal, setActionModal] = useState(null); // 'APPROVE', 'REJECT'
    const [lwdDate, setLwdDate] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Checklist specific
    const [isAddingTask, setIsAddingTask] = useState(false);
    const [newTaskName, setNewTaskName] = useState('');

    const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('zTaskAuthToken')}` } };

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/offboarding/all`, authHeader);
            setRequests(res.data);
            if (selectedReq) {
                const updated = res.data.find(r => r.id === selectedReq.id);
                if (updated) setSelectedReq(updated);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchChecklist = async (id) => {
        try {
            const res = await axios.get(`${API_BASE}/offboarding/${id}/checklist`, authHeader);
            setChecklist(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchDocs = async (id) => {
        try {
            const res = await axios.get(`${API_BASE}/offboarding/${id}/documents`, authHeader);
            setDocs(res.data);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, []);

    const handleSelect = (req) => {
        setSelectedReq(req);
        fetchChecklist(req.id);
        fetchDocs(req.id);
        const dateStr = req.tentative_lwd ? new Date(req.tentative_lwd).toISOString().split('T')[0] : '';
        setLwdDate(dateStr);
    };

    const handleUpdateStatus = async (status) => {
        try {
            const res = await axios.put(`${API_BASE}/offboarding/${selectedReq.id}/status`, {
                action: status,
                approvedLwd: status === 'APPROVE' ? lwdDate : null,
                rejectionReason: status === 'REJECT' ? rejectReason : null
            }, authHeader);

            toast.success(res.data.message);
            setActionModal(null);
            fetchRequests();
        } catch (e) {
            toast.error(e.response?.data?.error || "Failed to update status");
        }
    };

    const handleChecklistToggle = async (itemId, currentStatus) => {
        try {
            await axios.put(`${API_BASE}/offboarding/checklist/${itemId}`, {
                isChecked: !currentStatus
            }, authHeader);
            fetchChecklist(selectedReq.id);
        } catch (e) {
            toast.error("Failed to update item");
        }
    };

    const handleAddChecklistItem = async () => {
        if (!newTaskName.trim()) return toast.warning("Task name cannot be empty");
        try {
            await axios.post(`${API_BASE}/offboarding/${selectedReq.id}/checklist`, {
                item_name: newTaskName
            }, authHeader);
            setNewTaskName('');
            setIsAddingTask(false);
            fetchChecklist(selectedReq.id);
            toast.success("Item added");
        } catch (e) {
            toast.error("Failed to add item");
        }
    };

    const handleDeleteChecklistItem = async (e, itemId) => {
        e.stopPropagation();
        if (!window.confirm("Remove this item?")) return;
        try {
            await axios.delete(`${API_BASE}/offboarding/checklist/${itemId}`, authHeader);
            fetchChecklist(selectedReq.id);
            toast.success("Item removed");
        } catch (e) {
            toast.error("Failed to delete item");
        }
    };

    const generateDocs = async () => {
        if (checklist.length === 0) return toast.error("Checklist not found.");
        if (checklist.some(i => !i.is_checked)) {
            return toast.warning("Please complete all checklist items first.");
        }
        try {
            toast.info("Generating documents...");
            await axios.post(`${API_BASE}/offboarding/${selectedReq.id}/generate-docs`, {}, authHeader);
            toast.success("Documents Generated & Offboarding Completed!");
            fetchRequests();
            fetchDocs(selectedReq.id);
        } catch (e) {
            toast.error(e.response?.data?.error || "Generation Failed");
        }
    };

    const sendEmail = async () => {
        try {
            toast.info("Sending email...");
            await axios.post(`${API_BASE}/offboarding/${selectedReq.id}/send-email`, {}, authHeader);
            toast.success("Documents sent to employee!");
        } catch (e) {
            toast.error(e.response?.data?.error || "Email Failed");
        }
    };

    const deactivateUser = async () => {
        if (!window.confirm("Are you sure you want to deactivate this user account?")) return;
        try {
            await axios.put(`${API_BASE}/offboarding/deactivate`, { userId: selectedReq.user_id }, authHeader);
            toast.success("User Deactivated.");
            fetchRequests();
        } catch (e) {
            toast.error("Deactivation Failed");
        }
    };

    const getInitials = (f, l) => {
        const first = f && f !== 'null' && f !== 'undefined' ? f[0] : '';
        const last = l && l !== 'null' && l !== 'undefined' ? l[0] : '';
        return (first + last || 'NA').toUpperCase();
    };

    const formatName = (f, l, code) => {
        const first = f && f !== 'null' && f !== 'undefined' ? f : '';
        const last = l && l !== 'null' && l !== 'undefined' ? l : '';
        const fullName = `${first} ${last}`.trim();
        if (fullName) return fullName;
        return code && code !== 'N/A' ? code : 'Unknown Employee';
    }

    const filteredRequests = requests.filter(req =>
        `${req.first_name} ${req.last_name} ${req.emp_code}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <div className="offboarding-page">
            <header className="offboarding-header">
                <div className="header-content">
                    <h1>Offboarding Management</h1>
                    <p className="subtitle">Track and manage employee departures</p>
                </div>
                <div className="header-actions">
                    <div className="header-stats-badge">
                        <Users size={16} />
                        <span>{requests.length} Records</span>
                    </div>
                </div>
            </header>

            <div className="content-wrapper">
                {/* List View */}
                <div className="list-section">
                    <div className="offboarding-search-container">
                        <IoSearch className="search-icon-premium" size={20} />
                        <input
                            className="search-input-premium"
                            placeholder="Search by name, email or employee code..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="offboarding-header-row offboarding-grid-config">
                        <div className="col-head">ID</div>
                        <div className="col-head">EMPLOYEE</div>
                        <div className="col-head">LWD</div>
                        <div className="col-head" style={{ textAlign: 'center' }}>STATUS</div>
                    </div>

                    <div className="list-scroll-area">
                        {loading ? (
                            <div style={{ padding: 100, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
                        ) : filteredRequests.length > 0 ? (
                            filteredRequests.map(req => (
                                <div
                                    key={req.id}
                                    className={`offboarding-employee-row offboarding-grid-config ${selectedReq?.id === req.id ? 'active' : ''}`}
                                    onClick={() => handleSelect(req)}
                                >
                                    <div className="row-id">{req.emp_code || 'NA'}</div>
                                    <div className="row-name-cell">
                                        <div className="avatar-sm">{getInitials(req.first_name, req.last_name)}</div>
                                        <div>
                                            <div className="name-text">{formatName(req.first_name, req.last_name, req.emp_code)}</div>
                                            <div className="dept-text">{req.department_name || 'General'} Team</div>
                                        </div>
                                    </div>
                                    <div style={{ fontWeight: 600, color: '#475569', fontSize: 13 }}>
                                        {formatDate(req.approved_lwd || req.tentative_lwd)}
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={`status-badge-premium ${req.status.toLowerCase()}`}>{req.status}</span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', padding: 100 }}>
                                <Users size={48} style={{ opacity: 0.1, marginBottom: 16 }} />
                                <div style={{ color: '#94A3B8', fontWeight: 600 }}>No matching resignation requests</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Details Side Panel */}
                {selectedReq && (
                    <div className="details-panel animate-slide-in">
                        <div className="panel-header">
                            <div className="panel-header-left" style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                <div className="avatar-lg">{getInitials(selectedReq.first_name, selectedReq.last_name)}</div>
                                <div className="panel-user-info">
                                    <h2>{formatName(selectedReq.first_name, selectedReq.last_name)}</h2>
                                    <span>{selectedReq.designation_name || 'Company Associate'}</span>
                                </div>
                            </div>
                            <button className="close-panel-btn" onClick={() => setSelectedReq(null)}><X size={24} strokeWidth={3} /></button>
                        </div>

                        <div className="panel-body">
                            {/* Summary Card */}
                            <div className="detail-section">
                                <div className="section-head">Status Snapshot</div>
                                <div className="status-card-premium">
                                    <div className="status-row">
                                        <span className="status-label-bold">{selectedReq.status}</span>
                                        <span className="percent-text">
                                            {selectedReq.status === 'COMPLETED' ? '100% Resolved' : (selectedReq.status === 'APPROVED' ? 'Cleared' : 'In Review')}
                                        </span>
                                    </div>
                                    <div className="prog-track">
                                        <div className="prog-fill" style={{ width: selectedReq.status === 'COMPLETED' ? '100%' : (selectedReq.status === 'APPROVED' ? '50%' : '15%') }}></div>
                                    </div>
                                </div>
                            </div>

                            {/* Details Grid */}
                            <div className="detail-section">
                                <div className="section-head">Departure Logistics</div>
                                <div style={{ background: '#F8FAFC', padding: 20, borderRadius: 16, border: '1px solid #F1F5F9' }}>
                                    <div style={{ marginBottom: 20 }}>
                                        <label style={{ fontSize: 10, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'block' }}>REASON FOR EXIT</label>
                                        <div style={{ fontStyle: 'italic', fontSize: 13, color: '#444', background: '#fff', padding: 14, borderRadius: 12, border: '1px solid #E2E8F0', lineHeight: 1.5 }}>
                                            "{selectedReq.reason || 'No reason provided by employee'}"
                                        </div>
                                    </div>
                                    <div className="info-grid-premium">
                                        <div className="info-box">
                                            <label>Notice period</label>
                                            <div className="val">{selectedReq.notice_period_days} Days</div>
                                        </div>
                                        <div className="info-box">
                                            <label>LWD (Tentative)</label>
                                            <div className="val">{formatDate(selectedReq.tentative_lwd)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Actions If Pending */}
                            {selectedReq.status === 'PENDING' && (
                                <div className="detail-section">
                                    <div className="section-head" style={{ color: '#F59E0B' }}>Action Required</div>
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <button className="btn-primary-premium" style={{ flex: 1, background: 'var(--success)' }} onClick={() => setActionModal('APPROVE')}>Approve Exit</button>
                                        <button className="btn-primary-premium" style={{ flex: 1, background: 'var(--danger)' }} onClick={() => setActionModal('REJECT')}>Reject</button>
                                    </div>
                                </div>
                            )}

                            {/* Checklist Section */}
                            {(selectedReq.status === 'APPROVED' || selectedReq.status === 'COMPLETED') && (
                                <div className="detail-section">
                                    <div className="section-head">
                                        <span>Exit Clearance</span>
                                        <button
                                            onClick={() => setIsAddingTask(true)}
                                            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '11px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                        >
                                            <IoAddCircleOutline size={14} /> Add Item
                                        </button>
                                    </div>

                                    <div className="checklist-container">
                                        {isAddingTask && (
                                            <div className="checklist-card" style={{ background: 'var(--primary-light)', border: '1px dashed var(--primary)', cursor: 'default' }}>
                                                <input
                                                    autoFocus
                                                    value={newTaskName}
                                                    onChange={e => setNewTaskName(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') handleAddChecklistItem(); else if (e.key === 'Escape') setIsAddingTask(false); }}
                                                    placeholder="Task description..."
                                                    style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, outline: 'none', fontWeight: 600 }}
                                                />
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <IoSaveOutline size={18} color="var(--success)" style={{ cursor: 'pointer' }} onClick={handleAddChecklistItem} />
                                                    <X size={20} strokeWidth={3} style={{ cursor: 'pointer', color: '#EF4444' }} onClick={() => setIsAddingTask(false)} />
                                                </div>
                                            </div>
                                        )}
                                        {checklist.map(item => (
                                            <div key={item.id} className={`checklist-card ${item.is_checked ? 'completed' : ''}`} onClick={() => handleChecklistToggle(item.id, item.is_checked)}>
                                                <div className="checkbox-icon">
                                                    {item.is_checked ? <IoCheckboxOutline size={20} color="var(--success)" /> : <IoSquareOutline size={20} color="#94A3B8" />}
                                                </div>
                                                <div className="checklist-text">{item.item_name}</div>
                                                <button className="btn-icon-delete" onClick={(e) => handleDeleteChecklistItem(e, item.id)}>
                                                    <IoTrashOutline size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    {selectedReq.status === 'APPROVED' && (
                                        <button
                                            className="btn-primary-premium"
                                            style={{ width: '100%', marginTop: 24 }}
                                            disabled={checklist.some(i => !i.is_checked)}
                                            onClick={generateDocs}
                                        >
                                            <FileText size={18} /> Finalize Paperwork
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Documents Section */}
                            {docs.length > 0 && (
                                <div className="detail-section">
                                    <div className="section-head">Exit Paperwork</div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        {docs.map(doc => (
                                            <a key={doc.id} href={doc.document_url} target="_blank" rel="noopener noreferrer" className="doc-card-premium">
                                                <div className="doc-icon-box"><FileText size={18} /></div>
                                                <div className="doc-info-main">
                                                    <span className="doc-name">{doc.document_type.replace(/_/g, ' ')}</span>
                                                    <span className="doc-date">Generated on {formatDate(doc.generated_at)}</span>
                                                </div>
                                                <Download size={14} color="var(--text-muted)" />
                                            </a>
                                        ))}
                                    </div>
                                    <button className="btn-terminate" style={{ marginTop: 12, borderColor: '#E2E8F0', color: '#64748B' }} onClick={sendEmail}>
                                        <Mail size={16} /> Send Copy to Employee
                                    </button>
                                </div>
                            )}

                            {/* Final Actions */}
                            {(selectedReq.status === 'APPROVED' || selectedReq.status === 'COMPLETED') && (
                                <div style={{ paddingTop: 10 }}>
                                    <button className="btn-terminate" onClick={deactivateUser}>
                                        <Trash2 size={16} /> Terminate System Access
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            {actionModal && (
                <div className="modal-overlay">
                    <div className="modal-content animate-pop-in">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>{actionModal === 'APPROVE' ? 'Confirm Exit' : 'Reject Request'}</h3>
                            <button onClick={() => setActionModal(null)} className="close-panel-btn"><X size={24} strokeWidth={3} /></button>
                        </div>

                        {actionModal === 'APPROVE' ? (
                            <div style={{ marginBottom: 24 }}>
                                <label className="form-label" style={{ marginBottom: 8, display: 'block', fontSize: 12, fontWeight: 800, color: 'var(--text-secondary)' }}>LAST WORKING DAY</label>
                                <input type="date" className="modern-input" style={{ width: '100%', height: 48, borderRadius: 12, padding: '0 16px', background: '#F8FAFC', border: '1px solid #E2E8F0' }} value={lwdDate} onChange={e => setLwdDate(e.target.value)} />
                                <div style={{ marginTop: 12, padding: 12, background: 'var(--primary-light)', borderRadius: 10, fontSize: 12, color: 'var(--primary)', display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <IoInformationCircleOutline size={16} /> LWD will be updated on approval.
                                </div>
                            </div>
                        ) : (
                            <div style={{ marginBottom: 24 }}>
                                <label className="form-label" style={{ marginBottom: 8, display: 'block', fontSize: 12, fontWeight: 800, color: 'var(--text-secondary)' }}>REJECTION RATIONALE</label>
                                <textarea className="modern-input" rows="4" style={{ height: 'auto', padding: 16, width: '100%', borderRadius: 12, background: '#F1F5F9', border: '1px solid #E2E8F0' }} placeholder="Explain why this request is being rejected..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 12 }}>
                            <button className="btn-terminate" style={{ flex: 1, borderColor: '#E2E8F0', color: '#64748B' }} onClick={() => setActionModal(null)}>Cancel</button>
                            <button
                                className="btn-primary-premium"
                                style={{ flex: 1, background: actionModal === 'APPROVE' ? 'var(--success)' : 'var(--danger)', boxShadow: 'none' }}
                                onClick={() => handleUpdateStatus(actionModal)}
                            >
                                {actionModal === 'APPROVE' ? 'Approve Exit' : 'Reject Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OffboardingDashboard;
